/**
 * RepConnect - Admin Management Interface
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Mail, Phone, Shield,
  Edit2, CheckCircle, Clock, UserX,
  ChevronRight, AlertCircle, Crown, Key
} from 'lucide-react';
import AdminDelegation from '../utils/adminDelegation';
import PlaceholderCustomerService from '../utils/placeholderCustomerService';

const AdminManagement = ({ currentUser }) => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const loadAdminUsers = useCallback(async () => {
    if (currentUser && currentUser.id) {
      try {
        // Determine the customer/physician ID to query
        // If user is office_staff, use delegating_for; otherwise use their own ID
        const customerId = currentUser.delegating_for || currentUser.id;

        // Load from Supabase admin_users table with user_profiles join
        const staffList = await PlaceholderCustomerService.getCustomerStaff(customerId);

        console.log('AdminManagement: Raw staffList from Supabase:', staffList);

        // Transform to match expected format
        const admins = staffList.map(item => {
          console.log('AdminManagement: item.staff =', item.staff);
          console.log('AdminManagement: item.staff?.title =', item.staff?.title);
          return {
            id: item.id,
            staffId: item.staff_id,
            name: item.staff ? `${item.staff.first_name} ${item.staff.last_name}` : 'Unknown',
            email: item.staff?.email || '',
            phone: item.staff?.phone || '',
            role: item.role,
            title: item.staff?.title || item.role, // Use user_profiles.title, fallback to role
            permissions: item.permissions ? Object.keys(item.permissions).filter(k => item.permissions[k]) : [],
            isActive: item.status === 'active',
            inviteStatus: item.status === 'active' ? 'accepted' : 'pending',
            createdAt: item.created_at
          };
        });

        setAdminUsers(admins);
      } catch (error) {
        console.error('Error loading admin users from Supabase:', error);
        // Fallback to localStorage if Supabase fails
        const admins = AdminDelegation.getAdminUsers(currentUser.id);
        setAdminUsers(admins);
      }
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadAdminUsers();
  }, [loadAdminUsers]);

  const handleInviteAdmin = (adminData) => {
    const newAdmin = AdminDelegation.createAdminUser(currentUser.id, {
      ...adminData,
      invitedBy: currentUser.name
    });
    
    if (newAdmin) {
      setAdminUsers(prev => [...prev, newAdmin]);
      setShowInviteModal(false);
    }
  };

  const handleDeactivateAdmin = (adminId) => {
    AdminDelegation.deactivateAdminUser(currentUser.id, adminId);
    loadAdminUsers();
  };

  const handleEditAdmin = (adminData) => {
    AdminDelegation.updateAdminUser(currentUser.id, selectedAdmin.id, adminData);
    loadAdminUsers();
    setShowEditModal(false);
    setSelectedAdmin(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return CheckCircle;
      case 'pending': return Clock;
      case 'expired': return AlertCircle;
      default: return Clock;
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Management</h1>
          <p className="text-gray-600 mt-2">
            Delegate portal access to your office staff
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          <span>Invite Admin</span>
        </button>
      </div>

      {/* Admin Users List */}
      <div className="grid gap-6">
        {adminUsers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Admin Users</h3>
            <p className="text-gray-600 mb-6">
              Invite your office staff to help manage your portal
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
            >
              Invite First Admin
            </button>
          </div>
        ) : (
          adminUsers.map(admin => {
            const StatusIcon = getStatusIcon(admin.inviteStatus);
            return (
              <div key={admin.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        {admin.inviteStatus === 'accepted' ? (
                          <Crown className="w-6 h-6 text-blue-600" />
                        ) : (
                          <Key className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {admin.name}
                          </h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(admin.inviteStatus)}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {admin.inviteStatus}
                          </span>
                        </div>
                        
                        <p className="text-blue-600 font-medium mb-1">{admin.title}</p>
                        
                        <div className="flex items-center text-gray-600 text-sm space-x-4">
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {admin.email}
                          </div>
                          {admin.phone && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 mr-1" />
                              {admin.phone}
                            </div>
                          )}
                        </div>
                        
                        {admin.inviteStatus === 'pending' && (
                          <p className="text-sm text-gray-500 mt-2">
                            Invited {new Date(admin.invitedAt).toLocaleDateString()}
                          </p>
                        )}
                        
                        {admin.lastLoginAt && (
                          <p className="text-sm text-gray-500 mt-2">
                            Last active: {new Date(admin.lastLoginAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedAdmin(admin);
                          setShowEditModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      
                      {admin.isActive ? (
                        <button
                          onClick={() => handleDeactivateAdmin(admin.id)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">Inactive</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Permissions Preview */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-600">
                        <Shield className="w-4 h-4 mr-1" />
                        <span>{admin.permissions?.length || 0} permissions granted</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Invite Admin Modal */}
      {showInviteModal && (
        <InviteAdminModal
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteAdmin}
        />
      )}

      {/* Edit Admin Modal */}
      {showEditModal && selectedAdmin && (
        <EditAdminModal
          admin={selectedAdmin}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAdmin(null);
          }}
          onSave={handleEditAdmin}
        />
      )}
    </div>
  );
};

const InviteAdminModal = ({ onClose, onInvite }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'scheduler',
    title: '',
    permissions: []
  });

  const handleRoleChange = (role) => {
    const defaultPermissions = AdminDelegation.getDefaultPermissions(role);
    setFormData(prev => ({
      ...prev,
      role,
      title: AdminDelegation.getRoleDisplayName(role),
      permissions: defaultPermissions
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onInvite(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Invite Admin User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="scheduler">Scheduler</option>
              <option value="lead_medical_assistant">Lead Medical Assistant</option>
              <option value="practice_coordinator">Practice Coordinator</option>
              <option value="office_manager">Office Manager</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Send Invite
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditAdminModal = ({ admin, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: admin.name,
    email: admin.email,
    phone: admin.phone || '',
    role: admin.role,
    title: admin.title,
    permissions: admin.permissions || [],
    isActive: admin.isActive
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Edit Admin User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permissions
            </label>
            <div className="space-y-2">
              {Object.entries(AdminDelegation.PERMISSIONS).map(([key, value]) => (
                <label key={value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(value)}
                    onChange={() => togglePermission(value)}
                    className="mr-2"
                  />
                  <span className="text-sm">{value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="mr-2"
              />
              <span className="text-sm font-medium">Active User</span>
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminManagement;